/* eslint-disable newline-after-var */
import React, { useContext, useState, useEffect } from 'react';
import { FormContext, FormSchemaFields } from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import FormModal from '../../Shared/form/modal/FormModal';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { tenantDoctypeGroupsApi } from '../../../utils/api/doctypeGroups';
import AppContext from '../../../contexts/AppContext';

interface ClientGroupModalProps {
  show: boolean;
  isAdd: boolean;
  group?: any;
  customDocTypes?: any[];
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
}

const reorder = (list: any[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  
return result;
};

const ClientGroupModal: React.FC<ClientGroupModalProps> = ({
  show,
  group,
  customDocTypes = [],
  onClose,
  onSubmit,
}) => {
  const formContext = useContext(FormContext);
  const [orderedFolders, setOrderedFolders] = useState<any[]>([]);
  const [showOrganize, setShowOrganize] = useState(false);
  const [availableOptions, setAvailableOptions] = useState<any[]>([]);
  const [defaultFolders, setDefaultFolders] = useState<any[]>([]);
  const appContext = useContext(AppContext);
  const user = appContext.get('user');

  const fetchAvailableFolders = async () => {

    const folders= await tenantDoctypeGroupsApi.getDocTypeGroupItems({docTypeGroupId: String( group?.id), tenantId: String(user?.tenantId)});

    const docTypes= folders.tenant_group_items.filter(
      (folder: any) => !['Fixed Details', 'Fixed Details Project', 'Active Site Maps'].includes(folder.doc_type?.name)
    ).map((folder: any) => {
      return {
        doctype_id: folder.doc_type_id,
        name: folder.doc_type?.name || folder.name,
      };
    });

    setDefaultFolders(folders.tenant_group_items.filter(
      (folder: any) => ['Fixed Details', 'Fixed Details Project', 'Active Site Maps'].includes(folder.doc_type?.name)
    ));

    const mergedFolders = [
      ...docTypes,
      ...customDocTypes.filter(
        (custom: any) =>
          !folders.tenant_group_items.some(
            (folder: any) => folder.doc_type_id === custom.id,
          ),
      ).map((custom: any) => ({
        doctype_id: custom.id,
        name: custom.name,
      })),
    ];

    setAvailableOptions(mergedFolders);

    return folders;
   
  }



  useEffect(() => {
    if ( group?.client_group_items) {

      fetchAvailableFolders();

      const folders = group.client_group_items
        .sort((a: any, b: any) => a.sort_order - b.sort_order)
        .map((item: any) => ({
          doc_type_id: item.doc_type_id,
          name: item.doc_type?.name || item.name,
          sort_order: item.sort_order,
        }));
      setOrderedFolders(folders);
      
      // Initialize form context with current folders
      formContext.set('folders', folders.map((f:any) => ({
        value: f.doc_type_id,
        label: f.name,
      })));
    }
  }, [group]);

  // Sync orderedFolders when form context folders change
  useEffect(() => {
    if ( formContext.value.folders) {
      const newFolders = formContext.value.folders.map((f: any, index: number) => ({
        doc_type_id: f.value,
        name: f.label,
        sort_order: index,
      }));
      setOrderedFolders(newFolders);
    }
  }, [formContext.value.folders]);

  if (!show) return null;

  const handleSubmit = async (formData: any) => {
   
      // Validate folders
      if (!formData.folders || formData.folders.length === 0) {
        toast.error('Please select at least one folder');
        
        return;
      }


      // Submit folders with updated order
      const foldersData = formData.folders.map((folder: any, index: number) => ({
        doc_type_id: folder.value,
        sort_order: index,
      }));

      foldersData.push(...defaultFolders.map((folder: any, index: number) => ({
        doc_type_id: folder.doc_type_id,
        sort_order: foldersData.length + index,
      })));

      

      await onSubmit(foldersData);
    
  };

  const handleOnDragEnd = (result: any) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const currentFolders = formContext.value.folders || [];
    const reordered = reorder(currentFolders, result.source.index, result.destination.index);
    formContext.set('folders', reordered);
  };

  const handleRemoveFolder = (folderToRemove: number) => {
    const currentFolders = formContext.value.folders || [];
    const updated = currentFolders.filter((folder: any) => folder.value !== folderToRemove);
    formContext.set('folders', updated);
  };

  const handleOrganizeClick = () => {
    if (!formContext.value.folders || formContext.value.folders.length === 0) {
      toast.warning('Please select folders first');
      
return;
    }
    setShowOrganize(true);
  };

  const getEditSchema = () => ({
    folders: {
      label: 'Group Folders',
      controlType: UIControlType.customTagSelect,
      isMulti: true,
      showSelectAll: true,
      placeholder: 'Select folders',
      options: availableOptions.map((folder: any) => ({
        value: folder.doctype_id,
        label: folder.name,
      })),
      labelKey: 'label',
      valueKey: 'value',
      validation: {
        required: true,
      },
      reorderEnabled: true,
      onReorderButtonClick: handleOrganizeClick,
    },
  });


  return (
    <FormModal
      modalProps={{
        title:  `Edit - ${group?.name}`,
        submitBtnText: 'Save',
      }}
      onSubmit={handleSubmit}
      onCancel={onClose}
    >
        <>
          <FormSchemaFields
            schema={getEditSchema()}
            onChange={formContext.set}
            formData={formContext.value}
            initialFormData={{
              folders: group?.client_group_items
                ?.sort((a: any, b: any) => a.sort_order - b.sort_order)
                .map((item: any) => ({
                  value: item.doc_type.id,
                  label: item.doc_type?.name || item.name,
                })) || [],
            }}
          />

          {showOrganize && orderedFolders.length > 0 && (
            <div className='organize-folders-section'>
              <h4 className='organize-title'>Organize Document Folders</h4>
              <p style={{ marginBottom: '1rem', color: '#666', fontSize: '14px' }}>
                Drag and drop to reorder the document folders
              </p>
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId='clientGroupFolders'>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className='organize-folders-list'
                    >
                      {orderedFolders.map((folder, index) => (
                        <Draggable
                          key={`${folder.doc_type_id}-${index}`}
                          draggableId={`${folder.doc_type_id}-${index}`}
                          index={index}
                        >
                          {(provide) => (
                            <div
                              ref={provide.innerRef}
                              {...provide.draggableProps}
                              {...provide.dragHandleProps}
                              className='organize-folder-item'
                              style={{
                                ...provide.draggableProps.style,
                                left: 'auto !important',
                                top: 'auto !important',
                                marginBottom: '8px',
                                padding: '12px',
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                              }}
                            >
                              <FontAwesomeIcon
                                icon='grip-lines'
                                size='sm'
                                className='drag-handle'
                                style={{ color: '#6c757d', cursor: 'grab' }}
                              />
                              <span className='folder-name' style={{ flex: 1 }}>
                                {folder.name}
                              </span>
                              <button
                                className='remove-folder-btn'
                                onClick={() => handleRemoveFolder(folder.doc_type_id)}
                                title='Remove'
                                type='button'
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#dc3545',
                                  cursor: 'pointer',
                                  padding: '4px 8px',
                                }}
                              >
                                <i className='fa fa-trash' aria-hidden='true' />
                              </button>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          )}
        </>
    </FormModal>
  );
};

export default ClientGroupModal;
